<template>
  <div v-if="isLoadingVariants" class="selection-loading">
    <Icon icon="fa6-solid:circle-notch" class="animate-spin text-2xl text-blue-500"/>
    <span>Varianten werden erzeugt…</span>
  </div>

  <div v-else-if="hasVariants" class="measure-selection">
    <div class="selection-toolbar">
      <div>
        <h3 class="text-base font-semibold text-slate-800">Takte zusammenstellen</h3>
        <p class="text-xs text-slate-500">
          Klicke auf einen Takt, um eine andere Griff-Variante zu wählen. Änderungen sind jederzeit möglich.
        </p>
      </div>
      <div class="selection-actions">
        <button type="button" class="btn-secondary" :disabled="isAssembling" @click="resetToDefault">
          <Icon icon="fa6-solid:wand-magic-sparkles"/>
          Auto-Auswahl
        </button>
        <button type="button" class="btn-primary" :disabled="isAssembling" @click="onAssemble">
          <Icon v-if="isAssembling" icon="fa6-solid:circle-notch" class="animate-spin"/>
          <Icon v-else icon="fa6-solid:check"/>
          {{ isAssembling ? 'Wird erstellt…' : 'Fertigstellen' }}
        </button>
      </div>
    </div>

    <div class="measure-strip">
      <MeasureTile
          v-for="(measureVariants, index) in measures"
          :key="measureVariants.measure_index"
          :measure="measureVariants"
          :default-variant="defaultSelection[index] ?? ''"
          :selected="selection[index] ?? ''"
          @update:selected="(value) => setVariant(index, value)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {storeToRefs} from 'pinia';
import {Icon} from '@iconify/vue';
import {useMeasureSelectionStore} from '@/stores/useMeasureSelectionStore';
import MeasureTile from './MeasureTile.vue';

const store = useMeasureSelectionStore();
const {measures, selection, defaultSelection, hasVariants, isLoadingVariants, isAssembling} = storeToRefs(store);
const {setVariant, resetToDefault, assemble} = store;

async function onAssemble(): Promise<void> {
  await assemble();
}
</script>

<style scoped>
@import 'tailwindcss';

.selection-loading {
  @apply flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white/80 p-8 text-slate-600;
}

.measure-selection {
  @apply rounded-xl border border-slate-200 bg-white/80 p-4 shadow-sm backdrop-blur-sm;
}

.selection-toolbar {
  @apply mb-4 flex flex-wrap items-start justify-between gap-3;
}

.selection-actions {
  @apply flex items-center gap-2;
}

.btn-secondary {
  @apply inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2
  text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50;
}

.btn-primary {
  @apply inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600
  px-4 py-2 text-sm font-semibold text-white shadow transition-all hover:shadow-md disabled:opacity-60;
}

.measure-strip {
  @apply grid gap-3;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
}
</style>
